import React, { useEffect } from 'react';
import { db, auth } from '../firebaseConfig';
import { fetchCallLogs, CallLogWithOutcomeData } from '../pages/Clients/Common/Functions';
import { useParams } from 'react-router-dom';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { useSubAgentAuth } from '../pages/Clients/SubAgent/SubAgentAuthContext';

// extra packages
// import WaveSurfer from 'wavesurfer.js';


interface UserData {
    id: string;
    companyId?: string;
    // Add other user fields here
    [key: string]: any; // For other user fields
}

const CallLogs = () => {
    // List of call types for which we want to show recording buttons
    // This can be easily modified to add or remove call types
    const RECORDING_ELIGIBLE_CALL_TYPES = [
        "INCOMING", 
        "OUTGOING"
    ];

    const { currentSubAgent } = useSubAgentAuth();

    const [logs, setLogs] = React.useState<CallLogWithOutcomeData[]>([]);
    const [hasMore, setHasMore] = React.useState(true);
    const [isLoading, setIsLoading] = React.useState(false);
    const [lastDocument, setLastDocument] = React.useState<{
        id: string;
        callLogDate: Timestamp;
    } | null>(null);

    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    const [audioState, setAudioState] = React.useState<{ action: string, audioUrl: string }>({
        action: 'pause',
        audioUrl: '',
    });
    const [progress, setProgress] = React.useState(0);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const progressBarRef = React.useRef<HTMLDivElement>(null);

    const [isPlaying, setIsPlaying] = React.useState(false);
    // const wavesurferRef = React.useRef<WaveSurfer | null>(null);

    // const onReady = (wavesurfer: WaveSurfer) => {
    //     wavesurferRef.current = wavesurfer;
    // };

    // const onPlayPause = () => {
    //     if (wavesurferRef.current) {
    //         wavesurferRef.current.playPause();
    //     }
    // };

    const getStatusStyles = (status: string) => {
        switch (status) {
            case "INCOMING":
                return "text-green-600";
            case "OUTGOING":
                return "text-red-600";
            case "MISSED":
                return "text-blue-600";
            default:
                return "";
        }
    };

    const leadId = useParams<{ leadId: string }>().leadId;


    const fetchLogs = async (isLoadingMore = false) => {
        const user = auth.currentUser;
        if (!user || isLoading || (!hasMore && isLoadingMore)) {
            return;
        }

        let userData: UserData | null = null;

        // Get user document
        if (currentSubAgent) {
            console.log('currentSubAgent', currentSubAgent.companyId, currentSubAgent.subAgentId);
            if (currentSubAgent.companyId && currentSubAgent.subAgentId) {
                const userDoc = await getDoc(doc(db, 'companies', currentSubAgent.companyId, 'subAgents', currentSubAgent.subAgentId));
                userData = userDoc.data() as UserData;
                console.log('userData', userData);
                
                if (!userData) {
                    console.error('User data not found');
                    return;
                }
            } else {
                console.error('Missing companyId or subAgentId in currentSubAgent');
                return;
            }
        } else {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            userData = userDoc.data() as UserData;
            console.log('userData', userData);
            if (!userData) {
                console.error('User data not found');
                return;
            }
        }

        setIsLoading(true);
        try {
            const response = await fetchCallLogs(db, userData, {
                leadId,
                pageSize: 6,
                ...(isLoadingMore && lastDocument && {
                    lastDocumentId: lastDocument?.id,
                    lastCallLogTime: lastDocument?.callLogDate,
                }),
            });

            if (response.data.hasMore === false) {
                setHasMore(false);
            }

            // Update last document for pagination
            const lastLog = response.data.lastDocument;

            if (lastLog) {
                setLastDocument({
                    id: lastLog.id,
                    callLogDate: lastLog.lastCallTimestamp,
                });
            }

            // Append new logs to existing ones if loading more
            setLogs(prev =>
                isLoadingMore ? [...prev, ...response.data.callLogs] : response.data.callLogs
            );
        } catch (error) {
            console.error('Error fetching call logs:', error);
        } finally {
            setIsLoading(false);
        }
    }

    // Handle scroll events
    const handleScroll = () => {
        if (!scrollContainerRef.current || isLoading || !hasMore) return;

        const container = scrollContainerRef.current;
        const scrollPosition = container.scrollTop + container.clientHeight;
        const scrollThreshold = container.scrollHeight - 200; // Load more when within 200px of bottom

        if (scrollPosition >= scrollThreshold) {
            fetchLogs(true);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, []);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleScroll);
            return () => scrollContainer.removeEventListener('scroll', handleScroll);
        }
    }, [isLoading, hasMore]);

    function formatFirebaseTimestamp(timestamp: Timestamp) {
        if (!timestamp || typeof timestamp.seconds !== 'number') {
            throw new Error("Invalid timestamp object");
        }

        // Convert seconds to milliseconds
        const date = new Date(timestamp.seconds * 1000);

        // Options for formatting the date
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        };

        // Format the date using Intl.DateTimeFormat
        const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);

        // Replace commas with the desired separator
        return formattedDate.replace(',', ' •');
    }

    // function to convert seconds to hours, minutes and seconds
    function secondsToHms(d: number) {
        d = Number(d);
        var h = Math.floor(d / 3600);
        var m = Math.floor(d % 3600 / 60);
        var s = Math.floor(d % 3600 % 60);

        var hDisplay = h > 0 ? h + (h == 1 ? " hour, " : " hr, ") : "";
        var mDisplay = m > 0 ? m + (m == 1 ? " minute, " : " min, ") : "";
        var sDisplay = s > 0 ? s + (s == 1 ? " second" : " sec") : "";
        return hDisplay + mDisplay + sDisplay || "0 sec";
    }

    useEffect(() => {
        // Create audio element when component mounts
        audioRef.current = new Audio();

        // Set up event listeners
        if (audioRef.current) {
            audioRef.current.addEventListener('timeupdate', updateProgress);
            audioRef.current.addEventListener('ended', handleAudioEnd);
        }

        // Cleanup event listeners when component unmounts
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.removeEventListener('timeupdate', updateProgress);
                audioRef.current.removeEventListener('ended', handleAudioEnd);
            }
        };
    }, []);

    const updateProgress = () => {
        if (audioRef.current) {
            const percentage = (audioRef.current.currentTime / audioRef.current.duration) * 100;
            setProgress(percentage);
        }
    };

    const handleAudioEnd = () => {
        setAudioState(
            {
                action: 'pause',
                audioUrl: '',
            }
        );
        setProgress(0);
    };

    const handleRecording = (recordingUrl: string | null, action: string) => {
        console.log('handleRecording called with URL:', recordingUrl, 'action:', action);
        if (!recordingUrl) {
            console.log('No recording URL provided');
            return;
        }

        if (action === 'play') {
            if (!audioRef.current) {
                console.log('Creating new audio element');
                audioRef.current = new Audio(recordingUrl);
                audioRef.current.addEventListener('timeupdate', updateProgress);
                audioRef.current.addEventListener('ended', handleAudioEnd);
            }
            
            console.log('Current audio state:', audioRef.current.paused ? 'paused' : 'playing', 'src:', audioRef.current.src);
            
            if (audioRef.current.paused || audioRef.current.src !== recordingUrl) {
                if (audioRef.current.src !== recordingUrl) {
                    console.log('Setting new audio source:', recordingUrl);
                    audioRef.current.src = recordingUrl;
                }
                
                // Add error handling for audio loading
                audioRef.current.onerror = (e) => {
                    console.error('Error loading audio:', e);
                    alert('Error loading audio file. The recording may not be accessible.');
                };
                
                const playPromise = audioRef.current.play();
                if (playPromise !== undefined) {
                    playPromise
                        .then(() => {
                            console.log('Audio playback started successfully');
                            setAudioState({
                                action: 'play',
                                audioUrl: recordingUrl,
                            });
                        })
                        .catch(error => {
                            console.error('Error playing audio:', error);
                            alert('Could not play the audio. This might be due to browser restrictions or CORS issues.');
                        });
                }
            }
        } else if (audioRef.current && action === 'pause') {
            console.log('Pausing audio');
            audioRef.current.pause();
            setAudioState({
                action: 'pause',
                audioUrl: '',
            });
        }
    };

    const handleProgressBarClick = (e: { clientX: number; }) => {
        if (!audioRef.current || !progressBarRef.current) return;

        const rect = progressBarRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const barWidth = rect.width;
        const clickPercentage = (clickX / barWidth) * 100;

        // Set the current time of the audio based on click position
        audioRef.current.currentTime = (clickPercentage / 100) * audioRef.current.duration;
        setProgress(clickPercentage);
    };



    return (
        <div className="w-full h-[650px] max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col" style={{ fontFamily: "Montserrat" }}>
            <style>
                {`
                .follow-ups-scroll::-webkit-scrollbar {
                    width: 8px;
                    border-radius: 20px;
                    height: 40px;
                }

                .follow-ups-scroll::-webkit-scrollbar-track {
                    background: #DAEBF3;
                    border-radius: 20px;
                }

                .follow-ups-scroll::-webkit-scrollbar-thumb {
                    background-color: #82b3c2;
                    border-radius: 20px;
                }

                .follow-ups-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: #82b3c2 #DAEBF3;
                    -ms-overflow-style: none;
                    overflow-y: auto;
                    border-radius: 20px;
                    overflow-x: hidden;
                }

                .follow-ups-scroll::-webkit-scrollbar-button {
                    display: none;
                    border-radius: 20px;
                }
                `}
            </style>

            <div className="bg-gray-100 p-2 flex items-center justify-center text-center rounded-t-2xl h-8">
                <h1 className="text-sm text-black font-medium">
                    Call History
                </h1>
            </div>

            <div className="flex-1 follow-ups-scroll" ref={scrollContainerRef}>
                <div className="p-3">
                    <div className="relative">
                        <div className="absolute left-1/2 top-0 h-full w-px bg-blue-400" />

                        <div className="space-y-3">
                            {logs.map((log, index) => (
                                <div key={log.id} className={`relative flex ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                                    <div className="absolute left-1/2 -ml-1">
                                        <div className="w-2 h-2 rounded-full bg-white border-2 border-blue-400" />
                                    </div>

                                    <div className={`w-1/2 ${index % 2 === 0 ? 'pr-4' : 'pl-4'}`}>
                                        <div className={`${index % 2 === 0 ? 'text-right' : 'text-left'}`}>
                                            <div className="text-xs text-gray-500">
                                                <span>{formatFirebaseTimestamp(log.callTimestamp)}</span>
                                            </div>

                                            <div className="mt-0.5 bg-white rounded-lg border border-gray-200 p-2 hover:shadow-sm transition-shadow">
                                                <h3 className="font-medium text-xs text-gray-900">
                                                    {log.outcomeName}
                                                </h3>
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                    <div className={getStatusStyles(log.type)}>Status: {log.type}</div>
                                                    <div>Duration: {secondsToHms(log.duration)}</div>
                                                </div>
                                                {/* Notes */}
                                                {log.notes && (
                                                    <div className="flex items-end justify-end text-xs text-slate-500 mt-0.5 font-semibold">
                                                        {/* <img src={`${process.env.PUBLIC_URL}/images/desktop/notes-svg.svg`} alt="" className='w-4 h-4' /> */}
                                                        <div>Quick Notes:
                                                            <span className='text-blue-500'> {log.notes}</span>
                                                        </div>
                                                    </div>)}

                                                <div className="text-xs text-gray-500 mt-0.5 font-semibold">
                                                    <div>{log.userName}</div>
                                                </div>

                                                {/* Recordings - Only show for eligible call types */}
                                                {(log?.recordingUrl || log?.callRecordingUrl) && RECORDING_ELIGIBLE_CALL_TYPES.includes(log.type) && (
                                                    <div className="mt-2 flex space-x-2">
                                                        <button className="rounded-full w-8 h-8 flex items-center justify-center pl-0.5 ring-2 ring-gray-100 focus:outline-none">
                                                            {audioState.action === 'play' && (audioState.audioUrl === log?.recordingUrl || audioState.audioUrl === log?.callRecordingUrl) ? (
                                                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                                                    onClick={() => handleRecording(log?.callRecordingUrl || log?.recordingUrl, 'pause')}>
                                                                    <rect x="6" y="4" width="4" height="16"></rect>
                                                                    <rect x="14" y="4" width="4" height="16"></rect>
                                                                </svg>
                                                            ) : (
                                                                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                                                    onClick={() => handleRecording(log?.callRecordingUrl || log?.recordingUrl, 'play')}>
                                                                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                                                </svg>
                                                            )}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Progress Bar - Only show for eligible call types */}
                                            {(log?.recordingUrl || log?.callRecordingUrl) && RECORDING_ELIGIBLE_CALL_TYPES.includes(log.type) && (
                                                <div className="-mt-1 mb-3 rounded-b-lg">
                                                    <div
                                                        className="relative h-1 bg-gray-200 rounded cursor-pointer"
                                                        ref={progressBarRef}
                                                        onClick={handleProgressBarClick}
                                                    >
                                                        <div
                                                            className="absolute h-full bg-red-500 flex items-center justify-end transition-all duration-100"
                                                            style={{ width: `${(audioState.audioUrl === log?.recordingUrl || audioState.audioUrl === log?.callRecordingUrl) ? progress : 0}%` }}
                                                        >
                                                            {progress > 0 && (
                                                                <div className="rounded-full w-3 h-3 bg-red-500 shadow transform -translate-y-1/5"></div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {isLoading && (

                        <div className="text-center py-2">

                            <div className="text-gray-500 text-xs">Loading more...</div>

                        </div>

                    )}

                    {!hasMore && logs.length > 0 && (

                        <div className="text-center py-2">

                            <div className="text-gray-500 text-xs">No more call logs</div>

                        </div>

                    )}
                </div>
            </div>
        </div>
    );
};

// const WavesurferPlayer = ({
//     height,
//     barGap,
//     barWidth,
//     waveColor,
//     progressColor,
//     url = '',
//     onReady,
//     onPlay,
//     onPause
// }: {
//     height?: number,
//     barGap?: number,
//     barWidth?: number,
//     waveColor?: string,
//     progressColor?: string,
//     url: string,
//     // onReady?: (wavesurfer: WaveSurfer) => void,
//     onPlay?: () => void,
//     onPause?: () => void
// }) => {
//     const waveformRef = React.useRef(null);
//     // const wavesurferRef = React.useRef<WaveSurfer | null>(null);
//     const [isReady, setIsReady] = React.useState(false);

//     // useEffect(() => {
//     //     // Create WaveSurfer instance
//     //     if (!waveformRef.current) return;
//     //     const wavesurfer = WaveSurfer.create({
//     //         container: waveformRef.current,
//     //         height: height,
//     //         barGap: barGap,
//     //         barWidth: barWidth,
//     //         waveColor: waveColor,
//     //         progressColor: progressColor,
//     //         cursorWidth: 0
//     //     });

//     //     // Store the instance
//     //     wavesurferRef.current = wavesurfer;

//     //     // Load audio file
//     //     wavesurfer.load(url);

//     //     // Handle events
//     //     wavesurfer.on('ready', () => {
//     //         setIsReady(true);
//     //         if (onReady) onReady(wavesurfer);
//     //     });

//     //     wavesurfer.on('play', () => {
//     //         if (onPlay) onPlay();
//     //     });

//     //     wavesurfer.on('pause', () => {
//     //         if (onPause) onPause();
//     //     });

//     //     // Cleanup
//     //     return () => {
//     //         if (wavesurferRef.current) {
//     //             // wavesurferRef.current.destroy();
//     //         }
//     //     };
//     // }, [url, height, barGap, barWidth, waveColor, progressColor, onReady, onPlay, onPause]);

//     return <div ref={waveformRef} />;
// };

export default CallLogs;